/**
 * Error severity levels for categorization and logging.
 */
export type ErrorSeverity = 'fatal' | 'error' | 'warning' | 'info' | 'debug';

/**
 * Error categories for grouping related errors.
 */
export type ErrorCategory = 'auth' | 'network' | 'validation' | 'state' | 'storage' | 'unknown';

/**
 * Context information for an error.
 */
export interface ErrorContext {
  category: ErrorCategory;
  severity: ErrorSeverity;
  userMessage?: string;
  technicalDetails?: Record<string, unknown>;
}

/**
 * Classify an error based on its type and message.
 * Returns appropriate category, severity, and user-friendly message.
 *
 * @param error - The error to classify
 * @returns Error context with category, severity, and user message
 */
export function classifyError(error: unknown): ErrorContext {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();

    // Supabase auth errors
    if (message.includes('invalid login credentials')) {
      return {
        category: 'auth',
        severity: 'error',
        userMessage: 'Incorrect email or password. Please try again.',
      };
    }

    if (message.includes('email not confirmed')) {
      return {
        category: 'auth',
        severity: 'error',
        userMessage: 'Please verify your email address before signing in.',
      };
    }

    if (message.includes('session') || message.includes('token')) {
      return {
        category: 'auth',
        severity: 'error',
        userMessage: 'Your session has expired. Please sign in again.',
      };
    }

    // Network errors
    if (
      message.includes('fetch') ||
      message.includes('network') ||
      message.includes('timeout') ||
      message.includes('cors')
    ) {
      return {
        category: 'network',
        severity: 'error',
        userMessage: 'Network error. Please check your connection and try again.',
      };
    }

    // Storage errors
    if (message.includes('storage') || message.includes('quota')) {
      return {
        category: 'storage',
        severity: 'error',
        userMessage: 'Storage error. Please free up space and try again.',
      };
    }
  }

  // Zod validation errors
  if (error && typeof error === 'object' && 'issues' in error) {
    return {
      category: 'validation',
      severity: 'warning',
      userMessage: 'Please check your input and try again.',
      technicalDetails: { issues: (error as { issues: unknown[] }).issues },
    };
  }

  // State/React Query errors
  if (error instanceof Error && error.message.toLowerCase().includes('state')) {
    return {
      category: 'state',
      severity: 'error',
      userMessage: 'Something went wrong. Please refresh and try again.',
    };
  }

  // Default unknown error
  return {
    category: 'unknown',
    severity: 'error',
    userMessage: 'Something went wrong. Please try again.',
  };
}

/**
 * Extract a user-friendly message from an error.
 *
 * @param error - The error to extract message from
 * @returns User-friendly error message
 */
export function getUserMessage(error: unknown): string {
  return classifyError(error).userMessage ?? 'Something went wrong. Please try again.';
}

/**
 * Extract technical details from an error for logging.
 *
 * @param error - The error to extract details from
 * @returns Technical error message
 */
export function getTechnicalMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

/**
 * Serialize an error for structured logging.
 *
 * @param error - The error to serialize
 * @returns Serialized error object
 */
export function serializeError(error: unknown): Record<string, unknown> {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
      cause: error.cause,
    };
  }

  if (typeof error === 'object' && error !== null) {
    return { ...error };
  }

  return { error: String(error) };
}
